/**
 * EntityFilterDropdown — Generic entity filter supporting flat (1 param) and
 * nested two-pane (N params) layouts. Renders the popup via React Portal so it
 * escapes any ancestor `overflow: hidden` context (e.g. the DataSheet card wrapper).
 *
 * FLAT MODE  (params.length === 1):
 *   Single option list with search. Used for Channel, Warehouse, Vendor, Status.
 *
 * NESTED MODE (params.length > 1):
 *   Left pane: parameter list with active-selection badge.
 *   Right pane: option list for the highlighted parameter.
 *   Used for Product (Category + Product Group + Active Status).
 */

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import { dataSheetTokens } from '../DataSheet/tokens';

const tokens = dataSheetTokens;

// ─── Public types ─────────────────────────────────────────────────────────────

/** Selection mode for a single parameter within the filter. */
export type FilterParamMode = 'multi' | 'single';

/** One filterable dimension (e.g. Category, Channel, Active Status). */
export type FilterParam = {
    /** Unique key used as the record key in FilterValue. */
    key: string;
    /** Human-readable label shown in the left pane / single pane. */
    label: string;
    /**
     * multi  — checkboxes; [] means "all / no filter".
     * single — radios;     [] means "all / no filter" (first option is the "All" implicit).
     */
    mode: FilterParamMode;
    /** Selectable options for this param. */
    options: { label: string; value: string }[];
    /** Placeholder shown in the search box for this param's options. */
    searchPlaceholder?: string;
};

/**
 * The filter state: maps each param key to its selected values.
 * [] means no filter on that param (= "All").
 */
export type FilterValue = Record<string, string[]>;

export type EntityFilterDropdownProps = {
    /** Display label on the trigger button (e.g. "Product", "Channel"). */
    label: string;
    /** Parameters to filter by. 1 param → flat; 2+ params → nested two-pane. */
    params: FilterParam[];
    /** Current selected values per param key. */
    value: FilterValue;
    /** Called whenever selection changes. */
    onChange: (value: FilterValue) => void;
    /** Compact mode — matches DataSheet control height. Default true. */
    compact?: boolean;
    /** Optional className on the trigger container. */
    className?: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Count total selected values across all params. */
function countActive(value: FilterValue): number {
    return Object.values(value).reduce((sum, arr) => sum + arr.length, 0);
}

/** Build trigger label text. */
function buildTriggerLabel(label: string, params: FilterParam[], value: FilterValue): string {
    const total = countActive(value);
    if (total === 0) return label;
    if (total === 1) {
        for (const param of params) {
            const vals = value[param.key] ?? [];
            if (vals.length === 1) {
                const opt = param.options.find((o) => o.value === vals[0]);
                return `${label} · ${opt?.label ?? vals[0]}`;
            }
        }
    }
    return `${label} · ${total} filters`;
}

// ─── Popup positioning ────────────────────────────────────────────────────────

type PopupStyle = {
    position: 'fixed';
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    zIndex: number;
    minWidth: number;
};

function computePopupStyle(
    triggerRect: DOMRect,
    popupWidth: number,
    popupHeight: number,
): PopupStyle {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const style: PopupStyle = {
        position: 'fixed',
        zIndex: 9999,
        minWidth: popupWidth,
    };

    // Vertical: prefer opening downward
    if (triggerRect.bottom + 4 + popupHeight <= vh) {
        style.top = triggerRect.bottom + 4;
    } else {
        // Open upward
        style.bottom = vh - triggerRect.top + 4;
    }

    // Horizontal: prefer aligning left edge with trigger
    if (triggerRect.left + popupWidth <= vw) {
        style.left = triggerRect.left;
    } else {
        // Align right edge with trigger's right edge
        style.right = vw - triggerRect.right;
    }

    return style;
}

// ─── Option list (shared by both panes) ───────────────────────────────────────

const OptionList: React.FC<{
    param: FilterParam;
    selected: string[];
    onToggle: (value: string) => void;
    onToggleAll: (values: string[], select: boolean) => void;
    searchTerm: string;
    onSearchChange: (term: string) => void;
}> = ({ param, selected, onToggle, onToggleAll, searchTerm, onSearchChange }) => {
    const filtered = param.options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const allFilteredSelected =
        filtered.length > 0 && filtered.every((opt) => selected.includes(opt.value));
    const showSelectAll = param.mode === 'multi' && filtered.length > 1;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Search */}
            <div style={{ padding: `${tokens.spacing.xs}px`, borderBottom: `1px solid ${tokens.colors.borderSubtle}` }}>
                <input
                    autoFocus
                    type="text"
                    placeholder={param.searchPlaceholder ?? `Search ${param.label.toLowerCase()}...`}
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        width: '100%',
                        padding: `${tokens.spacing.xxs}px ${tokens.spacing.xs}px`,
                        fontSize: 11,
                        border: `1px solid ${tokens.colors.border}`,
                        borderRadius: 4,
                        backgroundColor: tokens.colors.headerBg,
                        color: tokens.colors.textPrimary,
                        outline: 'none',
                        boxSizing: 'border-box',
                    }}
                />
            </div>

            {/* Options */}
            <ul
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    margin: 0,
                    padding: `${tokens.spacing.xxs}px 0`,
                    listStyle: 'none',
                    maxHeight: 240,
                }}
            >
                {filtered.length === 0 && (
                    <li
                        style={{
                            padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                            fontSize: 11,
                            color: tokens.colors.textMuted,
                            textAlign: 'center',
                        }}
                    >
                        No options found
                    </li>
                )}
                {showSelectAll && (
                    <li>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleAll(
                                    filtered.map((o) => o.value),
                                    !allFilteredSelected,
                                );
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: tokens.spacing.xs,
                                width: '100%',
                                padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                fontSize: 12,
                                fontWeight: 500,
                                color: 'rgb(1, 119, 204)',
                                backgroundColor: 'transparent',
                                border: 'none',
                                borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'background-color 0.1s',
                            }}
                            onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = tokens.colors.hoverRow;
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                            }}
                        >
                            <span
                                style={{
                                    flexShrink: 0,
                                    width: 14,
                                    height: 14,
                                    borderRadius: 3,
                                    border: `1.5px solid ${allFilteredSelected ? 'rgb(1, 149, 255)' : tokens.colors.border}`,
                                    backgroundColor: allFilteredSelected ? 'rgb(1, 149, 255)' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {allFilteredSelected && (
                                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                                        <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span>
                                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                                {searchTerm.trim() && ` (${filtered.length})`}
                            </span>
                        </button>
                    </li>
                )}
                {filtered.map((opt) => {
                    const isChecked = selected.includes(opt.value);
                    return (
                        <li key={opt.value}>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(opt.value);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: tokens.spacing.xs,
                                    width: '100%',
                                    padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                    fontSize: 12,
                                    fontWeight: isChecked ? 500 : 400,
                                    color: isChecked ? 'rgb(1, 119, 204)' : tokens.colors.textPrimary,
                                    backgroundColor: isChecked ? 'rgba(1, 149, 255, 0.06)' : 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'background-color 0.1s',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isChecked) {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = tokens.colors.hoverRow;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isChecked) {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                {/* Checkbox / Radio visual */}
                                <span
                                    style={{
                                        flexShrink: 0,
                                        width: 14,
                                        height: 14,
                                        borderRadius: param.mode === 'single' ? '50%' : 3,
                                        border: `1.5px solid ${isChecked ? 'rgb(1, 149, 255)' : tokens.colors.border}`,
                                        backgroundColor: isChecked ? 'rgb(1, 149, 255)' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {isChecked && (
                                        param.mode === 'single' ? (
                                            // Radio dot
                                            <span
                                                style={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#fff',
                                                }}
                                            />
                                        ) : (
                                            // Checkbox tick
                                            <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                                                <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )
                                    )}
                                </span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {opt.label}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const EntityFilterDropdown: React.FC<EntityFilterDropdownProps> = ({
    label,
    params,
    value,
    onChange,
    compact = true,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const [activeParamKey, setActiveParamKey] = useState<string>(params[0]?.key ?? '');
    const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
    const [popupStyle, setPopupStyle] = useState<PopupStyle | null>(null);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const isNested = params.length > 1;
    const isActive = countActive(value) > 0;

    // Popup dimensions
    const FLAT_WIDTH = 220;
    const NESTED_WIDTH = 380;
    const POPUP_HEIGHT = 340; // estimated max height

    const popupWidth = isNested ? NESTED_WIDTH : FLAT_WIDTH;

    // ── Position calculation ──────────────────────────────────────────────────

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupStyle(computePopupStyle(rect, popupWidth, POPUP_HEIGHT));
    }, [popupWidth]);

    // ── Open / close ──────────────────────────────────────────────────────────

    const openPopup = useCallback(() => {
        setActiveParamKey(params[0]?.key ?? '');
        setSearchTerms({});
        updatePosition();
        setOpen(true);
    }, [params, updatePosition]);

    const closePopup = useCallback(() => {
        setOpen(false);
    }, []);

    const toggleOpen = useCallback(() => {
        if (open) closePopup();
        else openPopup();
    }, [open, openPopup, closePopup]);

    // Re-position on scroll or resize while open
    useEffect(() => {
        if (!open) return;
        const handler = (e: Event) => {
            // Don't close when scrolling inside the popup itself
            if (popupRef.current?.contains(e.target as Node)) return;
            closePopup();
        };
        const resizeHandler = () => updatePosition();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', resizeHandler);
        };
    }, [open, closePopup, updatePosition]);

    // Outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (
                triggerRef.current?.contains(e.target as Node) ||
                popupRef.current?.contains(e.target as Node)
            ) {
                return;
            }
            closePopup();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, closePopup]);

    // Recalculate position after popup renders (so we know actual height)
    useLayoutEffect(() => {
        if (open && popupRef.current && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const actualHeight = popupRef.current.getBoundingClientRect().height;
            setPopupStyle(computePopupStyle(rect, popupWidth, actualHeight));
        }
    }, [open, popupWidth]);

    // ── Selection handlers ────────────────────────────────────────────────────

    const handleToggle = useCallback((paramKey: string, optionValue: string) => {
        const param = params.find((p) => p.key === paramKey);
        if (!param) return;

        const current = value[paramKey] ?? [];

        let next: string[];
        if (param.mode === 'single') {
            // radio: selecting same value clears it (back to "all")
            next = current.includes(optionValue) ? [] : [optionValue];
        } else {
            // checkbox: toggle individual value
            next = current.includes(optionValue)
                ? current.filter((v) => v !== optionValue)
                : [...current, optionValue];
        }

        onChange({ ...value, [paramKey]: next });
    }, [params, value, onChange]);

    const handleToggleAll = useCallback((paramKey: string, values: string[], select: boolean) => {
        const current = value[paramKey] ?? [];
        const next = select
            ? Array.from(new Set([...current, ...values]))
            : current.filter((v) => !values.includes(v));
        onChange({ ...value, [paramKey]: next });
    }, [value, onChange]);

    const handleClearAll = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const cleared: FilterValue = {};
        for (const p of params) {
            cleared[p.key] = [];
        }
        onChange(cleared);
    }, [params, onChange]);

    const handleClearParam = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        closePopup();
        const cleared: FilterValue = {};
        for (const p of params) {
            cleared[p.key] = [];
        }
        onChange(cleared);
    }, [params, onChange, closePopup]);

    // ── Render ────────────────────────────────────────────────────────────────

    const activeParam = params.find((p) => p.key === activeParamKey) ?? params[0];
    const controlHeight = compact ? tokens.dimensions.controlHeight : 36;

    const triggerLabel = buildTriggerLabel(label, params, value);

    // ── Popup content ─────────────────────────────────────────────────────────

    const popup = open && popupStyle ? ReactDOM.createPortal(
        <div
            ref={popupRef}
            style={{
                ...popupStyle,
                backgroundColor: tokens.colors.surface,
                border: `1px solid ${tokens.colors.border}`,
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {isNested ? (
                // ─── NESTED TWO-PANE ───────────────────────────────────────
                <>
                    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Left pane — parameter list */}
                        <div
                            style={{
                                width: 150,
                                flexShrink: 0,
                                borderRight: `1px solid ${tokens.colors.border}`,
                                overflowY: 'auto',
                                padding: `${tokens.spacing.xs}px 0`,
                            }}
                        >
                            {params.map((param) => {
                                const paramActiveCount = (value[param.key] ?? []).length;
                                const isHighlighted = param.key === activeParamKey;
                                return (
                                    <button
                                        key={param.key}
                                        type="button"
                                        onClick={() => {
                                            setActiveParamKey(param.key);
                                            setSearchTerms((prev) => ({ ...prev, [param.key]: '' }));
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                            fontSize: 12,
                                            fontWeight: isHighlighted ? 600 : 400,
                                            color: isHighlighted ? 'rgb(1, 119, 204)' : tokens.colors.textPrimary,
                                            backgroundColor: isHighlighted ? 'rgba(1, 149, 255, 0.08)' : 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            borderLeft: isHighlighted
                                                ? '2px solid rgb(1, 149, 255)'
                                                : '2px solid transparent',
                                        }}
                                    >
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {param.label}
                                        </span>
                                        {paramActiveCount > 0 && (
                                            <span
                                                style={{
                                                    flexShrink: 0,
                                                    marginLeft: 4,
                                                    padding: '1px 5px',
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    color: '#fff',
                                                    backgroundColor: 'rgb(1, 149, 255)',
                                                    borderRadius: 8,
                                                }}
                                            >
                                                {paramActiveCount}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Right pane — option list for active param */}
                        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <div
                                style={{
                                    padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                    borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    textTransform: 'uppercase' as const,
                                    letterSpacing: '0.05em',
                                    color: tokens.colors.textSecondary,
                                }}
                            >
                                {activeParam?.label}
                            </div>
                            {activeParam && (
                                <OptionList
                                    param={activeParam}
                                    selected={value[activeParam.key] ?? []}
                                    onToggle={(v) => handleToggle(activeParam.key, v)}
                                    onToggleAll={(values, select) => handleToggleAll(activeParam.key, values, select)}
                                    searchTerm={searchTerms[activeParam.key] ?? ''}
                                    onSearchChange={(term) =>
                                        setSearchTerms((prev) => ({ ...prev, [activeParam.key]: term }))
                                    }
                                />
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                            borderTop: `1px solid ${tokens.colors.borderSubtle}`,
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleClearAll}
                            style={{
                                fontSize: 11,
                                color: countActive(value) > 0 ? 'rgb(1, 119, 204)' : tokens.colors.textMuted,
                                background: 'none',
                                border: 'none',
                                cursor: countActive(value) > 0 ? 'pointer' : 'default',
                                padding: 0,
                            }}
                        >
                            Clear all
                        </button>
                        <button
                            type="button"
                            onClick={closePopup}
                            style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: tokens.colors.textPrimary,
                                background: 'none',
                                border: `1px solid ${tokens.colors.border}`,
                                borderRadius: 4,
                                padding: `2px ${tokens.spacing.xs}px`,
                                cursor: 'pointer',
                            }}
                        >
                            Close
                        </button>
                    </div>
                </>
            ) : (
                // ─── FLAT SINGLE-PANE ──────────────────────────────────────
                activeParam && (
                    <>
                        <OptionList
                            param={activeParam}
                            selected={value[activeParam.key] ?? []}
                            onToggle={(v) => handleToggle(activeParam.key, v)}
                            onToggleAll={(values, select) => handleToggleAll(activeParam.key, values, select)}
                            searchTerm={searchTerms[activeParam.key] ?? ''}
                            onSearchChange={(term) =>
                                setSearchTerms((prev) => ({ ...prev, [activeParam.key]: term }))
                            }
                        />
                        {/* Footer for flat mode */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
                                borderTop: `1px solid ${tokens.colors.borderSubtle}`,
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleClearAll}
                                style={{
                                    fontSize: 11,
                                    color: (value[activeParam.key]?.length ?? 0) > 0
                                        ? 'rgb(1, 119, 204)'
                                        : tokens.colors.textMuted,
                                    background: 'none',
                                    border: 'none',
                                    cursor: (value[activeParam.key]?.length ?? 0) > 0 ? 'pointer' : 'default',
                                    padding: 0,
                                }}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={closePopup}
                                style={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: tokens.colors.textPrimary,
                                    background: 'none',
                                    border: `1px solid ${tokens.colors.border}`,
                                    borderRadius: 4,
                                    padding: `2px ${tokens.spacing.xs}px`,
                                    cursor: 'pointer',
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )
            )}
        </div>,
        document.body,
    ) : null;

    return (
        <div className={className} style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
            {/* Trigger button */}
            <button
                ref={triggerRef}
                type="button"
                onClick={toggleOpen}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: tokens.spacing.xs,
                    height: controlHeight,
                    padding: `0 ${tokens.spacing.sm}px`,
                    fontSize: 12,
                    fontWeight: 500,
                    color: isActive ? 'rgb(1, 119, 204)' : tokens.colors.textPrimary,
                    backgroundColor: isActive ? 'rgba(1, 149, 255, 0.06)' : tokens.colors.surface,
                    border: `1px solid ${open ? 'rgb(1, 149, 255)' : isActive ? 'rgba(1, 149, 255, 0.3)' : tokens.colors.border}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap' as const,
                    transition: 'all 0.15s ease',
                    boxShadow: open ? '0 0 0 2px rgba(1, 149, 255, 0.1)' : 'none',
                    maxWidth: 220,
                    overflow: 'hidden',
                }}
            >
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' as const,
                        flex: 1,
                        textAlign: 'left',
                    }}
                >
                    {triggerLabel}
                </span>

                {/* Clear × button when active */}
                {isActive && !open && (
                    <span
                        role="button"
                        onClick={handleClearParam}
                        style={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(1, 149, 255, 0.15)',
                            color: 'rgb(1, 119, 204)',
                            fontSize: 10,
                            lineHeight: 1,
                            cursor: 'pointer',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        ×
                    </span>
                )}

                {/* Chevron */}
                <span
                    style={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        color: tokens.colors.textMuted,
                        transition: 'transform 0.15s ease',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                    <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                        <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
            </button>

            {/* Portal popup */}
            {popup}
        </div>
    );
};

export default EntityFilterDropdown;
